import React, { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowDownRight, ExternalLink, Trash2, Loader2, Pencil, RotateCcw, Lock, Unlock, DollarSign } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import TradingViewChart from "./TradingViewChart";
import TVWidget from "./TVWidget";
import ZoneVisualizer, { ZoneBadge, fmtPrice } from "./ZoneVisualizer";
import CoachChat from "./CoachChat";

// Re-fetch interval for live price (Zone Map white line)
const PRICE_REFRESH_MS = 60_000;

export default function SetupDetailView({ setupId, onDeleted, onChanged, onEdit, embedded = false }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const s = await api.getSetup(setupId);
      setSetup(s);
    } catch {
      if (!silent) toast.error("Setup konnte nicht geladen werden.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh live price every 60s for the Zone Map
    if (refreshTimer.current) clearInterval(refreshTimer.current);
    refreshTimer.current = setInterval(() => load(true), PRICE_REFRESH_MS);
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupId]);

  const handleDelete = async () => {
    if (!window.confirm(`Setup "${setup.asset_name}" wirklich löschen?`)) return;
    await api.deleteSetup(setup.id);
    toast.success("Setup gelöscht.");
    onDeleted?.();
  };

  const handleResetTriggers = async () => {
    await api.resetTriggers(setup.id);
    toast.success("Trigger zurückgesetzt.");
    load(); onChanged?.();
  };

  const handleSetFilledEntry = async () => {
    const val = window.prompt(`Tatsächlicher Entry-Preis für ${setup.asset_name}:`, setup.filled_entry_price || "");
    if (val === null) return;
    const num = parseFloat(val);
    if (isNaN(num) && val !== "") { toast.error("Ungültige Zahl."); return; }
    await api.updateSetup(setup.id, { filled_entry_price: val === "" ? null : num });
    toast.success("Entry-Preis aktualisiert.");
    load(); onChanged?.();
  };

  const handleClose = async () => {
    const val = window.prompt(`Exit-Preis für ${setup.asset_name} (leer = aktueller Preis ${setup.last_price || "?"}):`, setup.last_price || "");
    if (val === null) return;
    const num = val === "" ? setup.last_price : parseFloat(val);
    if (isNaN(num)) { toast.error("Ungültiger Preis."); return; }
    await api.updateSetup(setup.id, { status: "closed", closed_exit_price: num });
    toast.success("Setup geschlossen.");
    load(); onChanged?.();
  };

  const handleReopen = async () => {
    await api.updateSetup(setup.id, { status: "active", closed_exit_price: null });
    toast.success("Setup wieder aktiv.");
    load(); onChanged?.();
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;
  if (!setup) return null;

  const isLong = setup.direction === "long";
  const DirIcon = isLong ? ArrowUpRight : ArrowDownRight;
  const dirColor = isLong ? "text-emerald-500" : "text-rose-500";

  const grouped = {
    entry: setup.zones.filter(z => z.type === "long_entry" || z.type === "short_entry"),
    target: setup.zones.filter(z => z.type === "target"),
    stop_loss: setup.zones.filter(z => z.type === "stop_loss"),
  };
  const triggeredCount = (setup.triggered_zone_ids || []).length;
  const closed = setup.status === "closed";
  const ch = setup.day_change_percent;
  const chColor = ch == null ? "text-zinc-500" : ch > 0 ? "text-emerald-400" : ch < 0 ? "text-rose-400" : "text-zinc-400";

  const tvSymbol = setup.symbol;

  return (
    <div className="space-y-4" data-testid="setup-detail-view">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-none flex items-center gap-2 truncate">
            {setup.asset_name}
            <span className={`inline-flex items-center gap-1 ${dirColor} text-base`}>
              <DirIcon size={20} strokeWidth={2.5} />
              <span className="uppercase">{setup.direction}</span>
            </span>
            {closed && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-800 text-zinc-300 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm">
                <Lock size={10} /> Closed
              </span>
            )}
          </h2>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleSetFilledEntry}
            className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-8" data-testid="btn-set-entry">
            <DollarSign size={12} className="mr-1" /> Entry
          </Button>
          {triggeredCount > 0 && !closed && (
            <Button variant="outline" size="sm" onClick={handleResetTriggers}
              className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-8" data-testid="btn-reset-triggers">
              <RotateCcw size={12} className="mr-1" /> Reset
            </Button>
          )}
          {closed ? (
            <Button variant="outline" size="sm" onClick={handleReopen}
              className="border-emerald-900 bg-transparent hover:bg-emerald-950/40 text-emerald-500 text-xs h-8" data-testid="btn-reopen">
              <Unlock size={12} className="mr-1" /> Öffnen
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleClose}
              className="border-amber-900 bg-transparent hover:bg-amber-950/40 text-amber-500 text-xs h-8" data-testid="btn-close">
              <Lock size={12} className="mr-1" /> Schließen
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onEdit?.(setup)}
            className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-8" data-testid="btn-edit-detail">
            <Pencil size={12} className="mr-1" /> Bearbeiten
          </Button>
          {setup.source_url && (
            <a href={setup.source_url} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="border-zinc-700 bg-transparent hover:bg-zinc-800 text-xs h-8">
                <ExternalLink size={12} className="mr-1" /> Quelle
              </Button>
            </a>
          )}
          <Button variant="outline" size="sm" onClick={handleDelete}
            className="border-rose-900 bg-transparent hover:bg-rose-950/40 text-rose-500 text-xs h-8" data-testid="btn-delete-detail">
            <Trash2 size={12} />
          </Button>
        </div>
      </div>

      {/* Sub header with price */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-mono-num text-zinc-500">{setup.symbol}</span>
        {setup.last_price && (
          <span className="px-2 py-0.5 bg-white text-black font-mono-num font-bold text-xs" data-testid="last-price">
            {fmtPrice(setup.last_price)}
          </span>
        )}
        {ch != null && (
          <span className={`font-mono-num text-xs ${chColor}`}>
            {ch > 0 ? "+" : ""}{ch.toFixed(2)}% heute
          </span>
        )}
        <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-display">
          {setup.category} · {setup.source_type === "friedrich" ? "Friedrich Report" :
            setup.source_type === "email" ? "E-Mail Inbound" : "Manuell"}
        </span>
        <span className="text-[10px] text-zinc-600 uppercase tracking-widest font-display ml-auto">
          Auto-Refresh 60s
        </span>
      </div>

      {/* === LARGE TRADINGVIEW CHART (TOP, FULL WIDTH) === */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-1">
          Live Chart · {setup.symbol}
        </div>
        <TradingViewChart symbol={setup.symbol} height={760} />
      </div>

      {/* Notes from email/report */}
      {setup.notes && (
        <div className="border border-zinc-800 bg-zinc-950 p-4">
          <div className="font-display text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
            Zusammenfassung aus dem Report
          </div>
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{setup.notes}</p>
        </div>
      )}

      {/* === COACH CHAT + ZONE MAP + ZONE LISTS (vor den Widgets) === */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 xl:col-span-7 space-y-3">
          <CoachChat setupId={setup.id} />
        </div>
        <div className="col-span-12 xl:col-span-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-1 flex items-center justify-between">
              <span>Zone Map</span>
              <span className="text-[9px] text-zinc-600 normal-case tracking-normal">Live · 60s Refresh</span>
            </div>
            <ZoneVisualizer zones={setup.zones} currentPrice={setup.last_price} />
          </div>
          <ZoneList title="Entry Zonen" zones={grouped.entry} triggered={setup.triggered_zone_ids || []} />
          <ZoneList title="Targets" zones={grouped.target} triggered={setup.triggered_zone_ids || []} />
          <ZoneList title="Stop Loss" zones={grouped.stop_loss} triggered={setup.triggered_zone_ids || []} />
        </div>
      </div>

      {/* === TV WIDGETS ROW (am Ende) === */}
      <div className="pt-2 border-t border-zinc-900">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-2">
          Markt-Daten · {tvSymbol}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-1">
              Symbol Overview
            </div>
            <TVWidget
              widgetType="symbol-overview"
              height={260}
              config={{
                symbols: [[setup.asset_name || tvSymbol, tvSymbol + "|1D"]],
                chartOnly: false,
                width: "100%",
                height: 260,
                locale: "de_DE",
                colorTheme: "dark",
                autosize: false,
                showVolume: false,
                showMA: false,
                hideDateRanges: false,
                hideMarketStatus: false,
                hideSymbolLogo: false,
                scalePosition: "right",
                scaleMode: "Normal",
                fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
                fontSize: "10",
                noTimeScale: false,
                valuesTracking: "1",
                changeMode: "price-and-percent",
                chartType: "area",
                lineColor: "rgba(16,185,129,1)",
                topColor: "rgba(16,185,129,0.3)",
                bottomColor: "rgba(16,185,129,0.0)",
                lineWidth: 2,
                gridLineColor: "rgba(39,39,42,0.4)",
                backgroundColor: "rgba(9,9,11,1)",
              }}
              testid="tvw-symbol-overview"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-1">
              Technische Analyse
            </div>
            <TVWidget
              widgetType="technical-analysis"
              height={400}
              config={{
                symbol: tvSymbol,
                interval: "1D",
                showIntervalTabs: true,
                displayMode: "single",
                width: "100%",
                height: 400,
              }}
              testid="tvw-technical"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-display mb-1">
              News &amp; Wirtschaftskalender
            </div>
            <div className="space-y-3">
              <TVWidget
                widgetType="timeline"
                height={200}
                config={{
                  feedMode: "symbol",
                  symbol: tvSymbol,
                  width: "100%",
                  height: 200,
                  displayMode: "regular",
                }}
                testid="tvw-news"
              />
              <TVWidget
                widgetType="events"
                height={190}
                config={{
                  width: "100%",
                  height: 190,
                  importanceFilter: "0,1",
                  countryFilter: "us,eu,de",
                }}
                testid="tvw-calendar"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ZoneList({ title, zones, triggered }) {
  if (zones.length === 0) return null;
  return (
    <div className="border border-zinc-800 bg-zinc-950">
      <div className="px-3 py-1.5 border-b border-zinc-800">
        <div className="font-display font-bold text-[11px] uppercase tracking-wider text-zinc-300">{title}</div>
      </div>
      <div className="p-2 space-y-1.5">
        {zones.map(z => (
          <div key={z.id} className="flex items-center justify-between gap-2 text-xs">
            <ZoneBadge zone={z} />
            <div className="flex items-center gap-1.5">
              {z.label && <span className="text-zinc-500 font-mono-num text-[10px]">{z.label}</span>}
              {triggered.includes(z.id) && (
                <span className="px-1 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] font-display font-bold uppercase rounded-sm">HIT</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
